/**
 * Modal confirmation dialog rendered into document.body via a portal, above all
 * other content (z-index 99999+). Shows a title, message, and cancel/confirm
 * buttons (labels customizable via cancelText/confirmText, defaulting to
 * "cancel"/"confirm"); the caller controls visibility by conditionally rendering it.
 * Used for destructive/irreversible actions across bounty, submission, search,
 * payments, and admin training-data pages.
 */
import { createPortal } from "react-dom";

export interface AlertDialogProps {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
    title,
    message,
    confirmText = "confirm",
    cancelText = "cancel",
    onConfirm,
    onCancel,
}) => {
    const dialog = (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50" style={{ zIndex: 99999 }} />

            {/* Dialog */}
            <div
                className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]"
                style={{ zIndex: 100000 }}
            >
                <div className="w-full max-w-md rounded-lg bg-slate-50 p-6 shadow-lg">
                    <h2 className="text-lg font-semibold font-porsui text-black mb-3">{title}</h2>
                    <p className="text-sm text-gray-600 font-porsui mb-6">{message}</p>
                    <div className="flex justify-end gap-4">
                        <button
                            onClick={onCancel}
                            className="rounded-md px-4 py-2 text-sm font-porsui text-black hover:bg-gray-100 transition-colors"
                        >
                            {cancelText}
                        </button>
                        <button
                            onClick={onConfirm}
                            className="rounded-md px-4 py-2 text-sm font-porsui text-black hover:bg-gray-100 transition-colors"
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );

    return createPortal(dialog, document.body);
};

export default AlertDialog;
